import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, vModelText as _vModelText, withModifiers as _withModifiers, withDirectives as _withDirectives, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-a7e5989a"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "editable-root" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = ["placeholder", "disabled", "onClick"]
const _hoisted_4 = { class: "label-mention" }
const _hoisted_5 = { class: "mention-list" }
const _hoisted_6 = ["onMouseenter", "onMousedown"]
const _hoisted_7 = { class: "user-image" }
const _hoisted_8 = ["src"]
const _hoisted_9 = { class: "user-name" }

import { computed, nextTick, onMounted, onUnmounted, ref, useAttrs, watch } from 'vue'
import { translate } from '@/languages/i18n'
import { addUserIconParams } from '@/use/usePath'

/** User item coming from parent */
type MentionUser = {
  id: string | number
  display_name: string
  image_url?: string
  user_type?: any
}

/** Mention range in *display text* (what user sees in textarea) */
type MentionRange = {
  id: string
  start: number // inclusive
  end: number // exclusive
  display: string
  type?: string
}

type HistoryState = {
  text: string
  mentions: MentionRange[]
  caret: number
}

export default /*#__PURE__*/_defineComponent({
  props: {
  users: {
    type: Array as () => MentionUser[],
    default: () => [],
    required: true,
  },
  className: {
    type: [String, Array, Object],
    default: '',
  },
  placeholder: {
    type: String,
    default: translate('placeholder'),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  titleMention: {
    type: String,
    default: '',
  },
},
  emits: ['content', 'eventEnter', 'getListUserMention', 'checkErrorOccurred'],
  setup(__props, { expose, emit }) {

const props = __props




const attrs = useAttrs()

/** ========== refs/state ========== */
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const mirrorRef = ref<HTMLDivElement | null>(null)
const mentionList = ref<HTMLElement | null>(null)

const text = ref<string>('') // display text (textarea value)
const mentions = ref<MentionRange[]>([]) // mention ranges in display text
const prevText = ref<string>('') // last value (for diffing on input)

const showMentionList = ref(false)
const mentionQuery = ref('')
const lastMentionQuery = ref('')
const selectedIndex = ref(0)

/** IME composition tracking (Japanese/Korean/Chinese input) */
const isComposing = ref(false)
const composingText = ref('')
const composingStart = ref<number | null>(null)
const composingEnd = ref<number | null>(null)

const isSpaceChar = (ch: string | undefined) => ch === ' ' || ch === '\u00A0' || ch === '\u3000'

// --- UNDO/REDO LOGIC ---
const historyStack = ref<HistoryState[]>([])
const redoStack = ref<HistoryState[]>([])
const MAX_HISTORY = 50 // Limit the number of steps saved to 50 to avoid slowing down the device.
const lastTypingTime = ref(0) // Used to combine recent keystrokes.

/** Mention list item refs (for scroll-into-view) */
const userListItemRefs = ref<(HTMLLIElement | null)[]>([])
const setUserListItemRef = (el: HTMLLIElement | null, index: number) => {
  userListItemRefs.value[index] = el
}

/** Fast lookup for validation + render */
const userById = computed(() => {
  const map = new Map<string, MentionUser>()
  props.users.forEach((u) => map.set(String(u.id), u))
  return map
})

/** ========== utils ========== */
const stripDiacritics = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
const normalizeForSearch = (s: string) => stripDiacritics(String(s).normalize('NFKC')).toLowerCase().replace(/\s+/g, '')

/** Escape HTML for mirror overlay */
function escapeHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;')
}

/** Keep spaces/newlines like textarea in the mirror overlay */
function textToHtml(s: string) {
  let html = escapeHtml(s).replace(/\n/g, '<br/>')

  if (s.endsWith('\n')) {
    html += '&#8203;'
  }

  return html
}

/** Always work with sorted mention ranges */
function sortMentions(ms: MentionRange[]) {
  return [...ms].sort((a, b) => a.start - b.start || a.end - b.end)
}

/** Tiny helper for replacing [start, end) in a string */
function replaceRange(value: string, start: number, end: number, insert: string) {
  return value.slice(0, start) + insert + value.slice(end)
}

/** Remove mentions that overlap [start, end) (we consider them "broken") */
function removeMentionsOverlapping(ms: MentionRange[], start: number, end: number) {
  return ms.filter((m) => m.end <= start || m.start >= end)
}

/** Shift mentions that start at/after `fromIndex` by `delta` */
function shiftMentionsAfter(ms: MentionRange[], fromIndex: number, delta: number) {
  if (delta === 0) return ms
  return ms.map((m) => (m.start >= fromIndex ? { ...m, start: m.start + delta, end: m.end + delta } : m))
}

/** Get textarea and selection in one place */
function getTextarea() {
  return textareaRef.value
}

function getSelection(ta: HTMLTextAreaElement) {
  const start = ta.selectionStart ?? 0
  const end = ta.selectionEnd ?? start
  return { start, end }
}

function setCaret(ta: HTMLTextAreaElement, pos: number, focus = true) {
  if (focus) ta.focus()
  ta.setSelectionRange(pos, pos)
}

/** Current text shown to user (includes IME composing text preview) */
const displayText = computed(() => {
  if (!isComposing.value) return text.value
  const start = composingStart.value ?? 0
  const end = composingEnd.value ?? start
  return replaceRange(text.value, start, end, composingText.value)
})

/** ========== Mention detection from textarea (display text) ========== */
/**
 * If caret is at "... @abc|", return the query "abc" and the [start,end) range to replace.
 * - Supports fullwidth "＠"
 * - Requires the @ to be at line start or preceded by whitespace (prevents emails like a@b.com)
 */
function extractMentionInfo(value: string, caret: number) {
  const before = value.slice(0, caret)
  const line = before.split('\n').pop() ?? ''
  const m = line.match(/(?:^|[\s\u00A0]|[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\u3400-\u4DBF\uAC00-\uD7AF\uFF66-\uFF9F])([@＠])([^\s@＠]*)$/)
  if (!m) return null

  const query = m[2] ?? ''
  const start = caret - query.length - 1 // '@' position
  const end = caret
  return { start, end, query }
}

/** ========== filtered users ========== */
const filteredUsers = computed(() => {
  const q = normalizeForSearch(mentionQuery.value)
  if (!q) return props.users
  return props.users.filter((u) => normalizeForSearch(u.display_name).includes(q))
})

/** ========== mirror rendering (highlight by mention ranges) ========== */
/**
 * Mirror is an overlay div that visually highlights mention ranges.
 * We render:
 *   normal text -> escaped HTML
 *   mention ranges -> <span class="mention-highlight">...</span>
 */
const mirrorHtml = computed(() => {
  const base = displayText.value

  // Get the original mention list
  let ms = sortMentions(mentions.value).map((m) => ({ ...m, type: 'mention' }))

  if (isComposing.value) {
    const cStart = composingStart.value ?? 0
    const cEnd = composingEnd.value ?? cStart
    const delta = composingText.value.length - (cEnd - cStart)

    // Reuse the built-in shiftMentionsAfter function to shift the mention.
    ms = shiftMentionsAfter(ms, cEnd, delta).map((m) => ({ ...m, type: 'mention' }))

    // Add a "Virtual Mention" representing the IME.
    if (composingText.value) {
      ms.push({
        id: 'ime-temp',
        start: cStart,
        end: cStart + composingText.value.length,
        display: composingText.value,
        type: 'ime',
      })
    }
    ms.sort((a, b) => a.start - b.start || a.end - b.end)
  }

  let html = ''
  let last = 0

  for (const m of ms) {
    if (m.start < 0 || m.end > base.length || m.start >= m.end) continue

    if (m.start > last) {
      html += textToHtml(base.slice(last, m.start))
    }

    const escapedContent = escapeHtml(base.slice(m.start, m.end))

    if (m.type === 'ime') {
      html += `<span class="ime-composing">${escapedContent}</span>`
    } else {
      html += `<span class="mention-highlight" data-user-id="${escapeHtml(m.id)}">@${escapeHtml(m.display)}</span>`
    }

    last = m.end
  }

  html += textToHtml(base.slice(last))
  return html || '<br/>'
})

/** ========== serialize display -> token ========== */
/**
 * Convert display string to backend token format:
 *   "Hello @Alice" -> "Hello @{123}"
 */
function serializeToToken(value: string, ms: MentionRange[]) {
  const sorted = sortMentions(ms)
  let out = ''
  let last = 0

  for (const m of sorted) {
    if (m.start < last) continue // overlap safety
    out += value.slice(last, m.start)
    out += `@{${m.id}}`
    last = m.end
  }

  out += value.slice(last)
  return out
}

/** Emit content + mentioned ids + raw highlight html */
function emitContent() {
  // normalize NBSP so server/content search works reliably
  const normalized = text.value.replace(/\u00A0/g, ' ')
  const tokenContent = serializeToToken(normalized, mentions.value)
  const ids = sortMentions(mentions.value).map((m) => Number(m.id))

  // validate ids exist in user list (prevents stale mentions)
  const errorOccurred = ids.some((id) => !userById.value.has(String(id)))
  emit('checkErrorOccurred', errorOccurred)

  emit('content', {
    content: tokenContent,
    mentionedUserIds: ids,
    rawContent: mirrorHtml.value,
  })
}

/** ========== mention list helpers ========== */
function scrollToActiveItem() {
  const activeItem = userListItemRefs.value[selectedIndex.value]
  activeItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
}

/**
 * Treat each mention as an "atomic block" when moving caret by ArrowLeft/ArrowRight.
 * - If caret is inside a mention, jump to nearest edge.
 * - If caret is at mention edge, jump across mention + its trailing space.
 */
function handleMentionArrowNavigation(e: KeyboardEvent) {
  const ta = getTextarea()
  if (!ta) return false

  const start = ta.selectionStart ?? 0
  const end = ta.selectionEnd ?? start
  const ms = sortMentions(mentions.value)

  for (const m of ms) {
    const after = afterMentionPos(m)

    // 🔹 SHIFT + ←
    if (e.shiftKey && e.key === 'ArrowLeft') {
      const probe = start - 1
      const m = findMentionContaining(probe)
      if (m) {
        e.preventDefault()
        ta.setSelectionRange(m.start, end)
        return true
      }
    }

    // 🔹 SHIFT + →
    if (e.shiftKey && e.key === 'ArrowRight') {
      if (end >= m.start && end < m.end) {
        e.preventDefault()
        ta.setSelectionRange(start, after)
        return true
      }
    }

    if (!e.shiftKey && start === end) {
      if (e.key === 'ArrowLeft' && start > m.start && start <= m.end) {
        e.preventDefault()
        setCaret(ta, m.start, false)
        return true
      }
      if (e.key === 'ArrowRight' && start >= m.start && start < m.end) {
        e.preventDefault()
        setCaret(ta, after, false)
        return true
      }
    }
  }

  return false
}

/** Position right after mention, optionally skipping its trailing space */
function afterMentionPos(m: MentionRange) {
  const ch = text.value[m.end]
  return m.end + (isSpaceChar(ch) ? 1 : 0)
}

/** Keyboard navigation when mention list is open */
function handleMentionListKey(event: KeyboardEvent) {
  if (!showMentionList.value || filteredUsers.value.length === 0) return false

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    selectedIndex.value = (selectedIndex.value + 1) % filteredUsers.value.length
    scrollToActiveItem()
    return true
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    selectedIndex.value = (selectedIndex.value - 1 + filteredUsers.value.length) % filteredUsers.value.length
    scrollToActiveItem()
    return true
  }

  if (event.key === 'Enter' || event.key === 'Tab') {
    event.preventDefault()
    const user = filteredUsers.value[selectedIndex.value]
    if (user) insertMention(user)
    return true
  }

  if (event.key === 'Escape') {
    event.preventDefault()
    closeMentionList()
    return true
  }

  return false
}

function closeMentionList() {
  showMentionList.value = false
  mentionQuery.value = ''
}

/** ========== insert mention ========== */
/**
 * Replace "@query" with "@DisplayName&nbsp;" and add a mention range.
 * Tricky part: if user is in IME composing, we "commit" the textarea value first.
 */
function insertMention(user: MentionUser) {
  saveToHistory()
  const ta = getTextarea()
  if (!ta) return

  const caret = ta.selectionStart ?? 0
  const baseValue = isComposing.value ? ta.value : text.value

  const info = extractMentionInfo(baseValue, caret)
  if (!info) return

  // IME: commit pending composing value into `text` + update mention ranges once
  if (isComposing.value) {
    ta.blur() // stop composition session
    updateMentionRangesOnInput(baseValue, text.value)
    text.value = baseValue
    prevText.value = baseValue

    isComposing.value = false
    composingText.value = ''
    composingStart.value = null
    composingEnd.value = null
  }

  const display = user.display_name
  const mentionText = `@${display}`
  const insertText = `${mentionText}\u00A0`

  const before = baseValue.slice(0, info.start)
  const after = baseValue.slice(info.end)
  const newText = before + insertText + after

  const start = before.length
  const end = start + mentionText.length
  const delta = insertText.length - (info.end - info.start)

  // 1) shift mentions after replaced range
  // 2) add new mention range
  mentions.value = sortMentions(
    shiftMentionsAfter(mentions.value, info.end, delta).concat([
      {
        id: String(user.id),
        start,
        end,
        display,
      },
    ])
  )

  text.value = newText

  nextTick(() => {
    setCaret(ta, start + insertText.length)
    snapCaretOutOfMention()
    prevText.value = text.value
    closeMentionList()
    emitContent()
  })
}

function selectMention(user: MentionUser) {
  insertMention(user)
}

/** ========== keep mention ranges in sync on typing/deleting ========== */
/**
 * Minimal diff algorithm:
 * - find common prefix & suffix
 * - the middle is the changed region
 * - any mention overlapping that region is removed
 * - mentions after the region are shifted by delta
 *
 * This keeps the logic fast even for long messages.
 */
function updateMentionRangesOnInput(newVal: string, oldVal: string) {
  if (newVal === oldVal) return

  const oldLen = oldVal.length
  const newLen = newVal.length

  // 1) common prefix
  let start = 0
  while (start < oldLen && start < newLen && oldVal[start] === newVal[start]) start++

  let endOld = oldLen
  let endNew = newLen
  while (endOld > start && endNew > start && oldVal[endOld - 1] === newVal[endNew - 1]) {
    endOld--
    endNew--
  }

  while (start > 0) {
    const isOverlapping = mentions.value.some((m) => m.end > start && m.start < endOld)
    if (!isOverlapping) break

    if (oldVal[start - 1] !== oldVal[endOld - 1]) break
    if (newVal[start - 1] !== newVal[endNew - 1]) break

    start--
    endOld--
    endNew--
  }

  const changeStart = start
  const changeEndOld = endOld
  const delta = endNew - endOld // newChangeLen - oldChangeLen

  mentions.value = shiftMentionsAfter(removeMentionsOverlapping(mentions.value, changeStart, changeEndOld), changeEndOld, delta)
}

/** Find mention containing a caret position (strictly inside) */
function findMentionAtIndex(index: number) {
  return mentions.value.find((m) => index > m.start && index < m.end) ?? null
}

function findMentionEndingAt(index: number) {
  return mentions.value.find((m) => m.end === index) ?? null
}

function findMentionStartingAt(index: number) {
  return mentions.value.find((m) => m.start === index) ?? null
}

/** Delete a mention as a single atomic block */
function deleteMentionRange(m: MentionRange) {
  saveToHistory()
  const ta = getTextarea()
  if (!ta) return

  text.value = replaceRange(text.value, m.start, m.end, '')
  const removedLen = m.end - m.start

  mentions.value = shiftMentionsAfter(
    mentions.value.filter((x) => x !== m),
    m.end,
    -removedLen
  )
  prevText.value = text.value

  nextTick(() => {
    setCaret(ta, m.start, false)
    emitContent()
  })
}

/**
 * Intercept Backspace/Delete so user can't partially delete a mention highlight.
 * - Backspace at " ... @Alice| " deletes the whole mention
 * - Delete at "|@Alice ... " deletes the whole mention
 */
function handleAtomicDelete(event: KeyboardEvent) {
  const ta = getTextarea()
  if (!ta) return false
  if (event.key !== 'Backspace' && event.key !== 'Delete') return false

  const s = ta.selectionStart ?? 0
  const e = ta.selectionEnd ?? 0
  if (s !== e) return false // allow normal deletion when selecting a range

  const inside = findMentionAtIndex(s)
  if (inside) {
    event.preventDefault()
    deleteMentionRange(inside)
    return true
  }

  if (event.key === 'Backspace') {
    const prev = findMentionEndingAt(s)
    if (prev) {
      event.preventDefault()
      deleteMentionRange(prev)
      return true
    }
  }

  if (event.key === 'Delete') {
    const next = findMentionStartingAt(s)
    if (next) {
      event.preventDefault()
      deleteMentionRange(next)
      return true
    }
  }

  return false
}

/**
 * If user clicks into the middle of a mention, we snap caret to nearest edge.
 * (Textarea selection is character-based, but mention should behave like a single token.)
 */
function snapCaretOutOfMention(e?: KeyboardEvent): boolean {
  const ta = textareaRef.value
  if (!ta) return false

  // Do not snap when the user is actively moving the caret.
  if (e && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
    return false
  }

  if (ta.selectionStart !== ta.selectionEnd) return false

  const pos = ta.selectionStart ?? 0
  const inside = findMentionContaining(pos)
  if (!inside) return false

  const target = inside.end
  ta.setSelectionRange(target, target)
  return true
}

function findMentionContaining(pos: number) {
  return mentions.value.find((m) => pos > m.start && pos < m.end) ?? null
}

function findMentionTouching(pos: number) {
  return mentions.value.find((m) => pos === m.start || pos === m.end) ?? null
}

/** True if caret is *inside* any mention (not at edges) */
function isCaretInsideExistingMention(pos: number) {
  return !!findMentionAtIndex(pos)
}

/** ========== detect mention list ========== */
/**
 * Open/close mention list depending on caret & current query.
 * Optional override params are used by IME composition preview.
 */
function detectMention(valueOverride?: string, caretOverride?: number, opts?: { triggerOnAt?: boolean }) {
  const ta = getTextarea()
  if (!ta) return

  let caret = caretOverride ?? ta.selectionStart ?? 0
  const value = valueOverride ?? (isComposing.value ? ta.value : text.value)

  // Align mentions with the `value` string coordinates (important during IME)
  let currentMentions = mentions.value
  if (isComposing.value && composingStart.value !== null) {
    const cStart = composingStart.value
    const cEnd = composingEnd.value ?? cStart
    const delta = composingText.value.length - (cEnd - cStart)
    currentMentions = shiftMentionsAfter(currentMentions, cEnd, delta)
  }

  const nextChar = value[caret]
  const startsMention = currentMentions.some((m) => m.start === caret)
  if ((nextChar === '@' || nextChar === '＠') && !startsMention) {
    closeMentionList()
    return
  }

  // Prevent list if caret is strictly inside a mention
  if (currentMentions.some((m) => caret > m.start && caret < m.end)) {
    closeMentionList()
    return
  }

  const info = extractMentionInfo(value, caret)
  if (!info) {
    closeMentionList()
    return
  }

  // Prevent list if the '@' symbol itself belongs to, or is immediately adjacent to the end of, an existing mention.
  const isInvalidAt = currentMentions.some((m) => info.start >= m.start && info.start <= m.end)
  if (isInvalidAt) {
    closeMentionList()
    return
  }

  if (opts?.triggerOnAt) {
    const ch = value[caret]
    if (ch === '@' || ch === '＠') caret = Math.min(caret + 1, value.length)
  }

  const q = info.query
  if (!showMentionList.value || q !== lastMentionQuery.value) selectedIndex.value = 0

  mentionQuery.value = q
  lastMentionQuery.value = q
  showMentionList.value = true

  nextTick(scrollToActiveItem)
}

/** ========== DOM handlers ========== */
function onFocus() {
  emit('getListUserMention')
  nextTick(() => detectMention(undefined, undefined, { triggerOnAt: true }))
}

function onClick() {
  if (snapCaretOutOfMention()) {
    closeMentionList()
    return
  }
  emit('getListUserMention')
  nextTick(() => {
    snapCaretOutOfMention()
    detectMention(undefined, undefined, { triggerOnAt: true })
    requestAnimationFrame(syncMirrorLayout)
  })
}

function onKeyup(e: KeyboardEvent) {
  // Enter is handled in keydown (submit)
  if (e.key === 'Enter') return
  if (e.shiftKey) return

  // When list is open, ignore navigation keys here (keydown already handled)
  if (showMentionList.value && ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) return

  if (['Backspace', 'Delete'].includes(e.key)) {
    if (!isComposing.value) {
      detectMention()
      snapCaretOutOfMention()
      syncMirrorLayout()
    }
    return
  }

  if (!isComposing.value) {
    detectMention()
    snapCaretOutOfMention(e)
    syncMirrorLayout()
  }
}

/** Instantly synchronize state from textarea to the text/mentions variable */
function syncInputState() {
  const ta = textareaRef.value
  if (!ta) return
  const currentVal = ta.value

  updateMentionRangesOnInput(currentVal, prevText.value)

  text.value = currentVal
  prevText.value = currentVal

  autoGrowAndKeepBottom()
  emitContent()
  requestAnimationFrame(syncMirrorLayout)
}

function onInput() {
  if (isComposing.value) return

  const ta = getTextarea()
  if (!ta) return

  nextTick(() => {
    syncInputState()
    snapCaretOutOfMention()
  })
}

function onKeydown(e: KeyboardEvent) {
  if (isComposing.value) return
  // 1. Check Undo/Redo
  if (handleUndoRedo(e)) return

  // 2. Save history
  handleHistoryRecording(e)

  // 3. The logic handles mentions (Navigation, Selection, Deletion of mentions).
  if (handleMentionArrowNavigation(e)) return
  if (handleMentionListKey(e)) return
  if (['Backspace', 'Delete'].includes(e.key)) {
    if (handleAtomicDelete(e)) return
  }
}

function onScroll() {
  const ta = getTextarea()
  const mirror = mirrorRef.value
  if (!ta || !mirror) return
  mirror.scrollTop = ta.scrollTop
  mirror.scrollLeft = ta.scrollLeft
}

function getMaxHeightPx(el: HTMLElement) {
  const mh = getComputedStyle(el).maxHeight
  const n = parseFloat(mh)
  return Number.isFinite(n) ? n : 165
}

function autoGrowAndKeepBottom() {
  const ta = textareaRef.value
  const mirror = mirrorRef.value
  if (!ta) return

  // 1. Save the current scrollTop to avoid being moved to the top of the page.
  const currentScrollTop = ta.scrollTop
  const maxH = getMaxHeightPx(ta)

  // 2. Reset height to measure actual scrollHeight.
  ta.style.height = 'auto'

  // 3. Calculate the new height
  const realScrollHeight = ta.scrollHeight
  const nextH = Math.min(realScrollHeight, maxH)
  ta.style.height = nextH + 'px'

  // 4. Smart Scroll Logic
  // If the user is typing at the end, or the content exceeds the max-height, we keep them at the end.
  // But if they are editing in the middle of a paragraph, DO NOT automatically scroll to the bottom.
  const isOverflow = realScrollHeight > maxH

  if (isOverflow) {
    // If the height changes (newline), prioritize scrolling according to the cursor.
    ta.scrollTop = currentScrollTop // Restore to its original position first.

    const { selectionStart } = ta
  }

  // 5. Sync mirror
  if (mirror) {
    mirror.style.height = ta.style.height // Sync height
    mirror.scrollTop = ta.scrollTop // Sync scroll
  }
}

let blurTimer: number | null = null

function onBlur() {
  // tiny delay so mousedown on list item can run before closing
  if (blurTimer) window.clearTimeout(blurTimer)
  blurTimer = window.setTimeout(() => closeMentionList(), 120)
}

/** ========== IME composition handlers (hard part) ========== */
/**
 * IME input fires composition events, where textarea value changes in special ways.
 * We keep a lightweight preview so mention detection still works for "@..." during IME.
 */
function onCompositionStart() {
  const ta = getTextarea()
  isComposing.value = true
  composingText.value = ''
  composingStart.value = ta ? ta.selectionStart ?? 0 : 0
  composingEnd.value = ta ? ta.selectionEnd ?? composingStart.value : composingStart.value
  closeMentionList()
}

function onCompositionUpdate(e: CompositionEvent) {
  const ta = getTextarea()
  if (!ta) return

  composingText.value = (e.data ?? '') as string

  const start = composingStart.value ?? ta.selectionStart ?? 0
  const end = composingEnd.value ?? start

  // previewValue is what user is seeing while composing (not always equal to `text.value`)
  let previewValue = ta.value.replace(/\r\n?/g, '\n')
  let caret: number

  const expectedLen = text.value.length - (end - start) + composingText.value.length

  if (previewValue.length !== expectedLen || previewValue === text.value) {
    if (composingText.value.length > 0) {
      // fallback preview (some browsers keep ta.value unchanged during composition, or lag 1 tick behind)
      previewValue = replaceRange(text.value, start, end, composingText.value)
      caret = start + composingText.value.length
    } else {
      detectMention()
      return
    }
  } else {
    // textarea already contains the composed string accurately
    const removedLen = end - start
    const delta = previewValue.length - (text.value.length - removedLen)
    caret = start + Math.max(0, delta)
  }

  // quick early exit: no @ before caret => no mention
  const beforeCaret = previewValue.slice(0, caret)
  if (!beforeCaret.includes('@') && !beforeCaret.includes('＠')) {
    closeMentionList()
    return
  }

  detectMention(previewValue, caret)
}

function onCompositionEnd() {
  isComposing.value = false
  composingText.value = ''
  composingStart.value = null
  composingEnd.value = null

  syncInputState()

  nextTick(() => {
    detectMention()
    snapCaretOutOfMention()
  })
}

/** ========== Paste handling for mention-highlight HTML ========== */
/**
 * We only intercept paste when clipboard HTML contains `.mention-highlight`.
 * Everything else uses native paste behavior.
 */

function onPaste(e: ClipboardEvent) {
  const ta = getTextarea()
  if (!ta) return

  const cd = e.clipboardData
  if (!cd) return

  const html = cd.getData('text/html')
  if (!html || !html.includes('mention-highlight')) return

  if (html && html.includes('mention-highlight')) {
    saveToHistory()
  }

  e.preventDefault()

  const savedScrollTop = ta.scrollTop

  const { start: startSel, end: endSel } = getSelection(ta)

  const isAtBottom = endSel >= text.value.length

  const doc = new DOMParser().parseFromString(html, 'text/html')

  let insertText = ''
  const pastedMentions: typeof mentions.value = []

  const cleanText = (s: string) =>
    (s ?? '')
      .replace(/\u00A0/g, ' ') // NBSP
      .replace(/\u200B/g, '') // ZWSP
      .replace(/\r/g, '')

  const append = (s: string) => {
    insertText += cleanText(s)
  }

  const findUserIdByDisplay = (display: string) => {
    const name = display.replace(/^@+/, '').trim()
    const exact = props.users?.find((u: any) => (u.display_name ?? '').trim() === name)
    if (exact) return String(exact.id)
    const lower = name.toLowerCase()
    const loose = props.users?.find((u: any) => (u.display_name ?? '').trim().toLowerCase() === lower)
    return loose ? String(loose.id) : null
  }

  doc.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? ''
      if (/^[\n\r]+$/.test(text)) return
      append(text)
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const el = node as HTMLElement

    if (el.tagName === 'BR') {
      append('\n')
      return
    }

    if (el.classList.contains('mention-highlight')) {
      let raw = cleanText(el.textContent ?? '').trim()
      raw = raw.replace(/^@+/, '')
      const display = `@${raw}`

      if (insertText.endsWith('@')) insertText = insertText.slice(0, -1)

      const mStart = insertText.length
      append(display)
      const mEnd = insertText.length

      const id = findUserIdByDisplay(display)
      if (id) {
        pastedMentions.push({
          id,
          start: mStart,
          end: mEnd,
          display: raw,
        })
      }
      return
    }

    append(el.textContent ?? '')
  })

  const beforeTrim = insertText

  const cutHead = beforeTrim.length - beforeTrim.replace(/^\s*\n+/, '').length
  if (cutHead > 0) {
    insertText = insertText.slice(cutHead)
    for (const m of pastedMentions) {
      m.start -= cutHead
      m.end -= cutHead
    }
  }

  const tailMatch = insertText.match(/\n+\s*$/)
  const cutTail = tailMatch ? tailMatch[0].length : 0
  if (cutTail > 0) {
    insertText = insertText.slice(0, insertText.length - cutTail)
  }

  if (!insertText) return

  const before = text.value.slice(0, startSel)
  const after = text.value.slice(endSel)
  const newText = before + insertText + after

  const insertPos = before.length
  const delta = insertText.length - (endSel - startSel)

  let nextMentions = removeMentionsOverlapping(mentions.value, startSel, endSel)
  nextMentions = shiftMentionsAfter(nextMentions, endSel, delta)

  pastedMentions.forEach((m) => {
    nextMentions.push({ ...m, start: insertPos + m.start, end: insertPos + m.end })
  })

  mentions.value = sortMentions(nextMentions)
  text.value = newText
  prevText.value = newText

  nextTick(() => {
    const caret = insertPos + insertText.length
    ta.focus()
    ta.setSelectionRange(caret, caret)
    autoGrowAndKeepBottom()

    requestAnimationFrame(() => {
      if (isAtBottom) {
        ta.scrollTop = ta.scrollHeight
      } else {
        ta.scrollTop = savedScrollTop
      }
      const mirror = mirrorRef.value
      if (mirror) {
        mirror.scrollTop = ta.scrollTop
        mirror.scrollLeft = ta.scrollLeft
      }
    })

    emitContent?.()
  })
}

/** ========== Copy handling for mention ========== */
function onCopy(e: ClipboardEvent) {
  const ta = getTextarea()
  if (!ta) return

  const cd = e.clipboardData
  if (!cd) return

  const { start, end } = getSelection(ta)
  if (start === end) return

  e.preventDefault()

  const plain = text.value.slice(start, end)
  const html = buildHtmlFromSelection(start, end)

  cd.setData('text/plain', plain)
  cd.setData('text/html', html)
}

function buildHtmlFromSelection(start: number, end: number) {
  let html = ''
  let cursor = start

  const selectedMentions = mentions.value.filter((m) => m.end > start && m.start < end).sort((a, b) => a.start - b.start)

  for (const m of selectedMentions) {
    // text before mention
    if (cursor < m.start) {
      html += escapeHtml(text.value.slice(cursor, m.start))
    }

    // mention span
    html += `<span class="mention-highlight">@${escapeHtml(m.display)}</span>`
    cursor = m.end
  }

  // text after mention
  if (cursor < end) {
    html += escapeHtml(text.value.slice(cursor, end))
  }

  // newline -> <br>
  return html.replace(/\n/g, '<br/>')
}

function syncMirrorLayout() {
  const ta = textareaRef.value
  const mirror = mirrorRef.value
  if (!ta || !mirror) return

  mirror.scrollTop = ta.scrollTop
  mirror.scrollLeft = ta.scrollLeft
  mirror.style.width = ta.style.width
}

/** ==================== Logic Undo/REDO ==================== */
/** Save the current state to history */
/**
 * Handles Undo (Ctrl+Z) and Redo (Ctrl+Y or Ctrl+Shift+Z) shortcuts
 * Returns true if the action has been performed so that onKeydown always returns
 */
function handleUndoRedo(e: KeyboardEvent): boolean {
  // It only runs when holding Ctrl (Win) or Command (Mac) and not Alt.
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return false

  const key = e.key.toLowerCase()

  // Case 1: Undo
  if (key === 'z' && !e.shiftKey) {
    e.preventDefault()
    undo()
    return true
  }

  // Case 2: Redo
  if (key === 'y' || (key === 'z' && e.shiftKey)) {
    e.preventDefault()
    redo()
    return true
  }

  return false
}

/**
 * Automatically saves typing history when the user types or deletes characters.
 */
function handleHistoryRecording(e: KeyboardEvent) {
  // Holding down Ctrl/Alt/Meta (command) does not count as normal typing.
  if (e.ctrlKey || e.altKey || e.metaKey) return

  // 1. Normal character keys (a, b, c, 1, 2...)
  if (e.key.length === 1) {
    recordTyping()
    return
  }

  // 2. Delete key (Backspace/Delete)
  if (e.key === 'Backspace' || e.key === 'Delete') {
    recordTyping()
  }
}
function saveToHistory() {
  const ta = getTextarea()
  if (!ta) return

  // 1. If the stack is too full, remove the oldest item.
  if (historyStack.value.length >= MAX_HISTORY) {
    historyStack.value.shift()
  }

  // 2. Save snapshot
  historyStack.value.push({
    text: text.value,
    mentions: JSON.parse(JSON.stringify(mentions.value)),
    caret: ta.selectionStart ?? 0,
  })

  // 3. Each time a new action is performed, delete the Redo stack.
  redoStack.value = []
}

/** Restore state (Used for both Undo and Redo) */
function applyState(state: HistoryState) {
  const ta = getTextarea()
  if (!ta) return

  // Update data
  text.value = state.text
  mentions.value = JSON.parse(JSON.stringify(state.mentions))
  prevText.value = state.text

  // Update interface
  nextTick(() => {
    ta.value = state.text // Force update DOM
    setCaret(ta, state.caret)
    autoGrowAndKeepBottom()
    emitContent()
    requestAnimationFrame(syncMirrorLayout)
  })
}

function undo() {
  if (historyStack.value.length === 0) return

  // 1. Save the current state to the Redo Stack before going back.
  const ta = getTextarea()
  redoStack.value.push({
    text: text.value,
    mentions: JSON.parse(JSON.stringify(mentions.value)),
    caret: ta?.selectionStart ?? 0,
  })

  // 2. Retrieve the previous state from History and Apply.
  const prevState = historyStack.value.pop()
  if (prevState) applyState(prevState)
}

function redo() {
  if (redoStack.value.length === 0) return

  // 1. Save the current state to the History Stack.
  const ta = getTextarea()
  historyStack.value.push({
    text: text.value,
    mentions: JSON.parse(JSON.stringify(mentions.value)),
    caret: ta?.selectionStart ?? 0,
  })

  // 2. Get the future state from the Redo Stack and Apply.
  const nextState = redoStack.value.pop()
  if (nextState) applyState(nextState)
}

/** Helper function to record keystroke history (Debounce) */
function recordTyping() {
  const now = Date.now()
  // If the previous typing was more than 1 second ago, or the history list is empty
  // -> Treat this as a new typing session -> Save the state BEFORE typing into history
  if (now - lastTypingTime.value > 1000 || historyStack.value.length === 0) {
    saveToHistory()
  }
  lastTypingTime.value = now
}

/** Close list when clicking outside (but keep it open when clicking inside list) */
function handleClickOutside(e: MouseEvent) {
  const target = e.target as Node
  const list = mentionList.value
  if (list && list.contains(target)) return
  closeMentionList()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

/** Keep validation accurate when props.users changes (don't emit content here to avoid loops) */
watch(
  () => props.users,
  () => {
    const ids = sortMentions(mentions.value).map((m) => m.id)
    const errorOccurred = ids.some((id) => !userById.value.has(id))
    emit('checkErrorOccurred', errorOccurred)
  },
  { deep: false, flush: 'post' }
)

expose({
  clear: () => {
    text.value = ''
    mentions.value = []
    prevText.value = ''
    closeMentionList()
    emitContent()
  },
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _mergeProps({ class: "chat-input mar-right-1" }, _unref(attrs)), [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", {
        ref: (_value, _refs) => {
          _refs['mirrorRef'] = _value
          mirrorRef.value = _value
        },
        class: _normalizeClass(["editable-div editable-mirror", [__props.className, __props.disabled ? 'disabled' : '']]),
        "aria-hidden": "true",
        innerHTML: _unref(mirrorHtml)
      }, null, 10, _hoisted_2),
      _withDirectives(_createElementVNode("textarea", {
        ref: (_value, _refs) => {
          _refs['textareaRef'] = _value
          textareaRef.value = _value
        },
        class: _normalizeClass(["editable-div editable-textarea", [__props.className, __props.disabled ? 'disabled' : '']]),
        placeholder: __props.placeholder,
        disabled: __props.disabled,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((text).value = $event)),
        onFocus: onFocus,
        onClick: _withModifiers(onClick, ["stop"]),
        onInput: onInput,
        onKeydown: onKeydown,
        onKeyup: onKeyup,
        onScroll: onScroll,
        onBlur: onBlur,
        onPaste: onPaste,
        onCopy: onCopy,
        onCompositionstart: onCompositionStart,
        onCompositionupdate: onCompositionUpdate,
        onCompositionend: onCompositionEnd
      }, null, 42, _hoisted_3), [
        [_vModelText, text.value]
      ])
    ]),
    (showMentionList.value && _unref(filteredUsers).length > 0)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "user-list-wrapper drop-up fs-75",
          ref: (_value, _refs) => {
            _refs['mentionList'] = _value
            mentionList.value = _value
          }
        }, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("span", null, _toDisplayString(__props.titleMention), 1)
          ]),
          _createElementVNode("ul", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filteredUsers), (user, index) => {
              return (_openBlock(), _createElementBlock("li", {
                key: user.id,
                class: _normalizeClass({ 'is-active': index === selectedIndex.value }),
                ref: (el) => setUserListItemRef(el as HTMLLIElement | null, index),
                onMouseenter: ($event: any) => (selectedIndex.value = index),
                onMousedown: _withModifiers(($event: any) => (selectMention(user)), ["prevent"])
              }, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("img", {
                    src: user?.image_url ? _unref(addUserIconParams)(user.image_url, user?.user_type) : require('@/assets/images/user-empty.svg'),
                    alt: "user avatar"
                  }, null, 8, _hoisted_8)
                ]),
                _createElementVNode("span", _hoisted_9, _toDisplayString(user?.display_name), 1)
              ], 42, _hoisted_6))
            }), 128))
          ])
        ], 512))
      : _createCommentVNode("", true)
  ], 16))
}
}

})